import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { fmtInputDate } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import Empty from "../../components/design/Empty";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { MdDeleteOutline } from "react-icons/md";

/**
 * 國定假日 — 逐字對應 DESIGN 稿 L412-L428 與 renderVals L1175。
 * 左欄假日清單（紅色日期方塊 + 刪除），右欄新增假日表單。
 */

/** 欄位上方的粗體小標 */
const LABEL_SX = { display: "block", fontWeight: 600, mb: 0.75 };

const WEEKDAY = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];

/** 用字串切割而非 new Date(str)，避免 UTC 解析讓日期整個差一天 */
function parseYmd(v) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(v || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function App(props) {
  const { toast, confirmDelete } = useApp();
  const today = useMemo(() => fmtInputDate(new Date()), []);
  const [year] = useState(() => new Date().getFullYear());
  const [name, setName] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);

  const fetchList = () => props.GET_HolidayList({ year }, null, null);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  /* 後端回 [{id,date:"yyyy-MM-dd",name,...}]；未回或格式異常時一律跳過該筆 */
  const { holidayList } = props;
  const holidays = useMemo(() => {
    const list = holidayList || [];
    if (!Array.isArray(list)) return [];
    return list
      .map((h) => {
        const raw = _.get(h, "date", _.get(h, "Date", ""));
        const d = parseYmd(raw);
        if (!d) return null;
        return {
          id: _.get(h, "id", _.get(h, "Id", null)),
          m: d.getMonth() + 1,
          d: d.getDate(),
          name: _.get(h, "name", _.get(h, "Name", "")) || "",
          weekday: WEEKDAY[d.getDay()],
          ymd: fmtInputDate(d),
        };
      })
      .filter(Boolean);
  }, [holidayList]);

  const onDelete = (h) => {
    if (h.id == null) return; // 後端尚未回 id 時無法刪除，直接忽略避免送出 null
    confirmDelete({
      title: "刪除假日？",
      subtitle: "此操作無法復原",
      body: `確定要刪除「${h.name}」（${h.ymd}）嗎？`,
      onConfirm: () => {
        props.DELETE_Holiday({ Ids: [h.id] }, () => {
          fetchList();
          toast("已刪除假日");
        }, null);
      },
    });
  };

  const onAdd = () => {
    const nm = name.trim();
    if (!nm) { toast("請輸入假日名稱", "error"); return; }
    const s = parseYmd(start);
    const e = parseYmd(end) || s;
    if (!s || !e) { toast("請選擇日期", "error"); return; }
    if (e < s) { toast("結束日期不可早於開始日期", "error"); return; }
    /* 後端 ReqPostHoliday 收的是日期陣列，起訖之間逐日展開（上限一年，避免誤填造成暴衝） */
    const dates = [];
    const cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
    while (cur <= e && dates.length < 366) {
      dates.push(fmtInputDate(cur));
      cur.setDate(cur.getDate() + 1);
    }
    props.POST_Holiday({ Dates: dates, Name: nm }, () => {
      setName("");
      fetchList();
      toast("已新增假日");
    }, null);
  };

  return (
    <Box className="pg" sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      {/* 左欄：假日清單 */}
      <Card sx={{ flex: 1, minWidth: 300 }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h3" component="h2">
            {year} 國定假日{" "}
            <Typography component="span" variant="inherit" color="text.secondary">
              {holidays.length}
            </Typography>
          </Typography>
        </Box>

        {holidays.length === 0 && <Empty title="尚無假日" variant="plain" pad="40px 20px" />}

        <List disablePadding>
          {holidays.map((h) => (
            <ListItem
              key={h.id != null ? `h${h.id}` : `${h.m}-${h.d}-${h.name}`}
              divider
              sx={{ gap: 1.75, px: 2.5, py: 1.5 }}
              secondaryAction={
                <Tooltip title="刪除">
                  <IconButton type="button" edge="end" color="error" aria-label="刪除" onClick={() => onDelete(h)}>
                    <MdDeleteOutline />
                  </IconButton>
                </Tooltip>
              }
            >
              {/* 紅色日期方塊（設計稿 L412） */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "var(--radius-md)",
                  bgcolor: "error.light",
                  color: "error.main",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography component="span" sx={{ fontWeight: 800, lineHeight: 1 }}>
                  {h.d}
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {h.m}月
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1">{h.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {h.weekday}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </Card>

      {/* 右欄：新增假日 */}
      <Card sx={{ width: 300, minWidth: 260, flex: 1, height: "fit-content" }}>
        <CardContent>
          <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
            新增假日
          </Typography>
          <Typography component="label" htmlFor="holiday-name" sx={LABEL_SX}>
            名稱
          </Typography>
          <TextField
            id="holiday-name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：中秋節"
            sx={{ mb: 2 }}
          />
          <Typography id="holiday-date-label" sx={LABEL_SX}>
            日期（單日或起訖）
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <TextField
              type="date"
              value={start}
              onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }}
              sx={{ flex: 1, minWidth: 150 }}
              slotProps={{ htmlInput: { "aria-labelledby": "holiday-date-label" } }}
            />
            <TextField
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              sx={{ flex: 1, minWidth: 150 }}
              slotProps={{ htmlInput: { "aria-labelledby": "holiday-date-label" } }}
            />
          </Box>
          <Button variant="contained" type="button" fullWidth onClick={onAdd}>
            新增
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  holidayList: _.get(state, "Holiday.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_HolidayList: (payload, callback, loading) => dispatch({ type: "GET_HolidayList", payload, callback, loading }),
  POST_Holiday: (payload, callback, loading) => dispatch({ type: "POST_Holiday", payload, callback, loading }),
  DELETE_Holiday: (payload, callback, loading) => dispatch({ type: "DELETE_Holiday", payload, callback, loading }),
});

App.propTypes = {
  holidayList: PropTypes.array,
  GET_HolidayList: PropTypes.func,
  POST_Holiday: PropTypes.func,
  DELETE_Holiday: PropTypes.func,
};

App.defaultProps = {
  holidayList: [],
  GET_HolidayList: () => {},
  POST_Holiday: () => {},
  DELETE_Holiday: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
